import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowUp, faArrowDown, faCheck, faUndo } from '@fortawesome/free-solid-svg-icons';
import './ColumnCustomization.css';

/**
 * Modal for customizing table column visibility and order
 */
const ColumnCustomizationModal = ({
  isOpen,
  onClose,
  columns,
  currentOrder,
  hiddenColumns,
  defaultHidden = [],  // Columns hidden by default (for Reset)
  onSave
}) => {
  const [items, setItems] = useState([]);
  const [localHidden, setLocalHidden] = useState(new Set());
  const wasOpenRef = useRef(false);

  // Column map for looking up details
  const columnMap = useMemo(() => {
    const map = new Map();
    columns?.forEach(col => map.set(col.key, col));
    return map;
  }, [columns]);

  // Initialize state when modal opens
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      const orderArray = currentOrder?.length > 0 
        ? [...currentOrder] 
        : columns?.map(c => c.key) || [];
      setItems(orderArray);
      setLocalHidden(new Set(hiddenColumns || []));
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, currentOrder, hiddenColumns, columns]);

  const handleMoveColumn = useCallback((index, direction) => {
    setItems(prev => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  }, []);

  // Toggle visibility
  const handleCheckboxChange = useCallback((columnKey, isCompulsory) => {
    if (isCompulsory) return;
    setLocalHidden(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  }, []);

  // Reset to defaults
  const handleReset = useCallback(() => {
    setItems(columns?.map(col => col.key) || []);
    setLocalHidden(new Set(defaultHidden));
  }, [columns, defaultHidden]);

  // Save
  const handleSave = useCallback(() => {
    onSave(items, localHidden);
  }, [items, localHidden, onSave]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="column-customization-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Customize Columns</h2>
          <button className="close-button" onClick={onClose} type="button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-content">
          <p className="modal-instructions">
            Use the arrows to reorder columns. Uncheck optional columns to hide them.
          </p>

          <div className="columns-list">
            {items.map((columnKey, index) => {
              const column = columnMap.get(columnKey);
              if (!column) return null;
              
              const isHidden = localHidden.has(columnKey);
              const isCompulsory = column.compulsory === true;

              return (
                <div key={columnKey} className="column-item">
                  <div className="column-reorder-controls">
                    <button
                      className="column-reorder-btn"
                      onClick={() => handleMoveColumn(index, -1)}
                      disabled={index === 0}
                      title="Move up"
                      type="button"
                    >
                      <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                    <button
                      className="column-reorder-btn"
                      onClick={() => handleMoveColumn(index, 1)}
                      disabled={index === items.length - 1}
                      title="Move down"
                      type="button"
                    >
                      <FontAwesomeIcon icon={faArrowDown} />
                    </button>
                  </div>
                    
                  <label className={`column-checkbox-label ${isCompulsory ? 'compulsory' : ''}`}>
                    <input
                      type="checkbox"
                      checked={!isHidden}
                      disabled={isCompulsory}
                      onChange={() => handleCheckboxChange(columnKey, isCompulsory)}
                      className="column-checkbox"
                    />
                    <span className="column-title">
                      {column.title}
                      {isCompulsory && <span className="compulsory-badge">Required</span>}
                    </span>
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        <div className="modal-actions">
          <button className="reset-button" onClick={handleReset} type="button">
            <FontAwesomeIcon icon={faUndo} /> Reset
          </button>
          <div className="modal-actions-right">
            <button className="cancel-button" onClick={onClose} type="button">
              Cancel
            </button>
            <button className="save-button" onClick={handleSave} type="button">
              <FontAwesomeIcon icon={faCheck} /> Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColumnCustomizationModal;
